import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Lock, CheckCircle, AlertCircle, Loader2, Eye, EyeOff, ShieldX, Clock } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

const ERROR_STATES = {
  invalid: {
    icon: ShieldX,
    title: "Invalid Reset Link",
    message: "This password reset link is not valid. Please request a new one.",
    color: "text-red-400"
  },
  expired: {
    icon: Clock,
    title: "Link Expired", 
    message: "This password reset link has expired. Please request a new one.",
    color: "text-amber-400"
  },
  used: {
    icon: CheckCircle,
    title: "Already Used",
    message: "This password reset link has already been used. Please sign in or request a new link.",
    color: "text-blue-400"
  },
  revoked: {
    icon: ShieldX,
    title: "Link Revoked",
    message: "This password reset link has been revoked.",
    color: "text-red-400"
  }
};

export default function PlatformOwnerResetPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [verifying, setVerifying] = useState(true);
  const [resetData, setResetData] = useState(null);
  const [errorType, setErrorType] = useState(null);
  const [formError, setFormError] = useState(null);
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Password validation rules (12+ chars, upper, lower, number, special)
  const passwordRules = {
    minLength: password.length >= 12,
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
  };
  const passwordValid = Object.values(passwordRules).every(Boolean);

  // Verify the reset token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setErrorType("invalid");
        setVerifying(false);
        return;
      }

      try {
        const { data } = await api.get(`/platform/owner-reset/verify/${token}`);
        setResetData(data);
      } catch (err) {
        const detail = err.response?.data?.detail?.toLowerCase() || "";
        
        if (detail.includes("already been used") || detail.includes("already used")) {
          setErrorType("used");
        } else if (detail.includes("revoked")) {
          setErrorType("revoked");
        } else if (detail.includes("expired")) {
          setErrorType("expired");
        } else {
          setErrorType("invalid");
        }
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    
    if (password !== confirmPassword) {
      setFormError("Passwords do not match");
      return;
    }
    
    if (!passwordValid) {
      setFormError("Password does not meet all requirements");
      return;
    }
    
    setSubmitting(true);
    
    try {
      await api.post("/platform/owner-reset/submit", {
        token,
        password,
        confirm_password: confirmPassword
      });
      
      // Clear passwords from state immediately
      setPassword("");
      setConfirmPassword("");
      setSuccess(true);
      
      // Redirect to admin login after delay
      setTimeout(() => {
        navigate("/admin", { state: { message: "Password updated successfully. Please sign in." } });
      }, 2500);
      
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      
      if (detail.toLowerCase().includes("expired")) {
        setErrorType("expired");
      } else if (detail.toLowerCase().includes("already") || detail.toLowerCase().includes("no longer valid")) {
        setErrorType("used");
      } else {
        setFormError(detail || "Failed to reset password. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-[#131E1E] flex items-center justify-center" data-testid="reset-loading">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-[#7EBDBC] animate-spin mx-auto mb-4" />
          <p className="text-white/50 text-sm">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (errorType) {
    const errorConfig = ERROR_STATES[errorType] || ERROR_STATES.invalid;
    const ErrorIcon = errorConfig.icon;
    
    return (
      <div className="min-h-screen bg-[#131E1E] flex items-center justify-center px-4" data-testid="reset-error">
        <div className="w-full max-w-sm text-center">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${
            errorType === "used" ? "bg-blue-500/10" : "bg-red-500/10"
          }`}>
            <ErrorIcon className={`w-8 h-8 ${errorConfig.color}`} />
          </div>
          <h1 className="text-xl font-medium text-white mb-2" style={H} data-testid="reset-error-title">
            {errorConfig.title}
          </h1>
          <p className="text-white/50 text-sm mb-6" data-testid="reset-error-message">
            {errorConfig.message}
          </p>
          <button
            onClick={() => navigate("/admin")}
            className="px-6 py-2.5 bg-[#205B5A] text-white rounded-full text-sm font-medium hover:bg-[#174544] transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-[#131E1E] flex items-center justify-center px-4" data-testid="reset-success">
        <div className="w-full max-w-sm text-center">
          <div className="w-16 h-16 bg-[#205B5A]/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-[#7EBDBC]" />
          </div>
          <h1 className="text-xl font-medium text-white mb-2" style={H}>Password Updated</h1>
          <p className="text-white/50 text-sm mb-4">
            Your password has been reset successfully.
          </p>
          <p className="text-white/30 text-xs">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen bg-[#131E1E] flex items-center justify-center px-4" data-testid="reset-form">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-[#205B5A]/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Lock className="w-5 h-5 text-[#7EBDBC]" />
          </div>
          <h1 className="text-xl font-medium text-white" style={H}>Reset Password</h1>
          <p className="text-xs text-white/50 mt-1">Platform Owner Account</p>
        </div>
        
        {/* Account Info */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
          <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Account</p>
          <p className="text-sm text-white font-medium" data-testid="reset-email">{resetData?.email}</p>
          {resetData?.expires_at && (
            <p className="text-[10px] text-white/30 mt-2">
              Link expires: {new Date(resetData.expires_at).toLocaleString("en-GB", { 
                day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" 
              })}
            </p>
          )}
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="reset-password-form">
          {formError && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-300 text-sm px-4 py-3 rounded-xl flex items-center gap-2" data-testid="form-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {formError}
            </div>
          )}
          
          <div>
            <label className="block text-xs text-white/50 mb-1">New Password *</label>
            <div className="relative">
              <input 
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="Enter new password"
                disabled={submitting}
                data-testid="password-input"
                className="w-full px-4 py-3 pr-10 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#7EBDBC] disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            
            {/* Password requirements */}
            {password && (
              <div className="mt-2 space-y-1" data-testid="password-requirements">
                <div className={`text-[10px] flex items-center gap-1 ${passwordRules.minLength ? 'text-[#7EBDBC]' : 'text-white/30'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${passwordRules.minLength ? 'bg-[#7EBDBC]' : 'bg-white/20'}`} />
                  At least 12 characters
                </div>
                <div className={`text-[10px] flex items-center gap-1 ${passwordRules.hasUpper ? 'text-[#7EBDBC]' : 'text-white/30'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${passwordRules.hasUpper ? 'bg-[#7EBDBC]' : 'bg-white/20'}`} />
                  Uppercase letter (A-Z)
                </div>
                <div className={`text-[10px] flex items-center gap-1 ${passwordRules.hasLower ? 'text-[#7EBDBC]' : 'text-white/30'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${passwordRules.hasLower ? 'bg-[#7EBDBC]' : 'bg-white/20'}`} />
                  Lowercase letter (a-z)
                </div>
                <div className={`text-[10px] flex items-center gap-1 ${passwordRules.hasNumber ? 'text-[#7EBDBC]' : 'text-white/30'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${passwordRules.hasNumber ? 'bg-[#7EBDBC]' : 'bg-white/20'}`} />
                  Number (0-9)
                </div>
                <div className={`text-[10px] flex items-center gap-1 ${passwordRules.hasSpecial ? 'text-[#7EBDBC]' : 'text-white/30'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${passwordRules.hasSpecial ? 'bg-[#7EBDBC]' : 'bg-white/20'}`} />
                  Special character (!@#$%^&*...)
                </div>
              </div>
            )}
          </div>
          
          <div>
            <label className="block text-xs text-white/50 mb-1">Confirm Password *</label>
            <div className="relative">
              <input 
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="Confirm new password"
                disabled={submitting}
                data-testid="confirm-password-input"
                className={`w-full px-4 py-3 pr-10 rounded-xl bg-white/5 border text-white text-sm placeholder-white/30 focus:outline-none disabled:opacity-50 ${
                  confirmPassword && confirmPassword !== password 
                    ? 'border-red-500/50 focus:border-red-500' 
                    : 'border-white/10 focus:border-[#7EBDBC]'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
                tabIndex={-1}
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {confirmPassword && confirmPassword !== password && (
              <p className="text-[10px] text-red-400 mt-1">Passwords do not match</p>
            )}
          </div>
          
          <button 
            type="submit" 
            disabled={submitting || !passwordValid || password !== confirmPassword}
            data-testid="reset-submit-btn"
            className="w-full bg-[#205B5A] text-white py-3 rounded-full font-medium text-sm hover:bg-[#174544] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Resetting Password..." : "Reset Password"}
          </button>
        </form>
        
        <p className="text-center text-[10px] text-white/20 mt-6">
          Remember your password?{" "}
          <button 
            onClick={() => navigate("/admin")} 
            className="text-[#7EBDBC] hover:underline"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
